import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    User,
    Edit,
    MapPin,
    Star,
    Shield,
    Link as LinkIcon,
    Check,
    X,
    Camera,
    MessageSquare,
    ChartAreaIcon,
    Clock,
    FileText,
    Home,
    Settings,
    Share2,
    LogOut,
    DollarSign,
    Award,
    Users,
    Briefcase,
    Eye,
    UserSearchIcon,
    Bookmark,
} from "lucide-react";
import OrientationLock from "../../../components/ui/OrientationLock";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import Experience from "../../user/components/ProfessionalProfileComponents/Experience";
import Portfolio from "../../user/components/ProfessionalProfileComponents/Portfolio";
import Certificate from "../../user/components/ProfessionalProfileComponents/Certificate";
import Education from "../../user/components/ProfessionalProfileComponents/Education";
import { useSelector } from "react-redux";
import {
    createUserCompany,
    getAllCurrencies,
    getAllLanguages,
    getAllTimezones,
    getUserCompanies,
    getUserGeneralSetting,
    getUserGeneralSettingAll,
    updateUserCompanies,
    UpdateUserProfile,
    uploadUserImage,
    useAppDispatch,
    validateVATNumber,
    getUserById,
    LogoutUser,
    LogoutManually,
    getUserJobs,
} from "../../../redux";
import { UpdateUserAvailabilities, getUserAvailabilities, getProfessionalStats } from "../../../redux/Actions/ProfessionalActions";
import EditProfileModal from "../../user/modals/EditProfileModal";
import Availability from "../../user/components/ProfessionalProfileComponents/Availability";
import Skills from "../../user/components/ProfessionalProfileComponents/Skills";
import SharedHeader4 from "../../../Headers/SharedHeader4";
import Spinner from "../../shared/components/Spinner";
import { hideLoader, selectLoaderValue, showLoader } from "../../../redux/Slices/loadingSlice";
import LocationComponent from "../../client/components/ClientProfileComponents/LocationComponent";
import { ValidationCompanyErrors, ValidationPersonalErrors } from "../../user";
import EmergencyAvailability from "../../user/components/ProfessionalProfileComponents/EmergencyAvailability";
import CategoryDisplay from "../../user/components/ProfessionalProfileComponents/CategoryDisplay";
import ServiceAreaDisplay from "../../user/components/ProfessionalProfileComponents/ServiceAreaDisplay";
import InternationalServiceAreas from "../../user/components/ProfessionalProfileComponents/InternationalServiceAreas";
import { ServiceArea } from "../../admin/models/ServiceArea.model";
import ContactComponent from "../../client/components/ClientProfileComponents/ContactComponent";
import LanguageComponent from "../../client/components/ClientProfileComponents/LanguageComponent";
import CompanyClientComponent from "../../client/components/ClientProfileComponents/CompanyClientComponent";
import { RoleEnum } from "../../shared/model";
import { GlobalService } from "../../shared/services";
import { ImportantInformationModal, EmergencyHoursModal } from "../../shared/components";
import { Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserCategoriesByType } from "../../../redux/Actions/UserCategoryActions";
import { UserCategoryTypeEnum } from "../../../redux/Slices/userCategorySlice";
import FavoriteButton from "../../../components/ui/FavoriteButton";
import SharedFooter3 from "../../../Footer/SharedFooter3";
import { fetchReviewsByUser } from "../../../redux/Slices/reviewSlice";
import { calculateUserAverageRating } from "../../../utils/ratingCalculationUtils";
import ReviewModal from "../../../components/ui/ReviewModal";
import ContractMilestoneModal from "../../../components/ui/ContractMilestoneModal";
import { fetchUserOffers } from "../../../redux/Slices/offerSlice";
import { getDynamicCurrencySymbol } from "../../../lib/currencyUtils";
import { getAchievementConfig } from "../../../redux/Actions/AchievementActions";
import { RootState } from "../../../redux/store";
import { useTranslation } from "react-i18next";

const MyProfile = ({ isAdminEdit = false, isViewing = false, adminUserId = null, onUserUpdated = null }) => {
    const { t, i18n } = useTranslation();
    const isLoading = useSelector(selectLoaderValue);
    const { company } = useSelector((state: any) => state.client);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editedProfile, setEditedProfile] = useState(null);
    const { user, generalSetting, allgeneralSetting, viewedUser } = useSelector((state: any) => state.auth);
    const { emergencyAvailabilities, availabilities, professionalStats, professionalStatsLoading } = useSelector((state: any) => state.professional);
    const { userCategories } = useSelector((state: any) => state.userCategory);
    const { userReviews, userReviewsLoading, userReviewsError } = useSelector((state: any) => state.reviews);
    const { professionalConfig } = useSelector((state: RootState) => state.achievements);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
    const [editingContact, setEditingContact] = useState(false);
    const [editingCompany, setEditingCompany] = useState(false);
    const [editingLocation, setEditingLocation] = useState(false);
    const [isLanguageEditing, setIsLanguageEditing] = useState(false);
    const [hasAddedNewLanguage, setHasAddedNewLanguage] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { userId: urlUserId } = useParams();

    // StarRatingDisplay component for showing user ratings
    const StarRatingDisplay = ({ rating, count, size = "sm" }: { rating: number; count: number; size?: "sm" | "md" | "lg" }) => {
        const sizeClasses = {
            sm: "h-4 w-4",
            md: "h-5 w-5",
            lg: "h-6 w-6"
        };

        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <div className="flex items-center gap-1">
                <div className="flex">
                    {[...Array(fullStars)].map((_, i) => (
                        <Star key={i} className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
                    ))}
                    {hasHalfStar && (
                        <div className="relative">
                            <Star className={`${sizeClasses[size]} text-gray-300`} />
                            <div className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                                <Star className={`${sizeClasses[size]} text-yellow-400 fill-current`} />
                            </div>
                        </div>
                    )}
                    {[...Array(emptyStars)].map((_, i) => (
                        <Star key={i + fullStars + (hasHalfStar ? 1 : 0)} className={`${sizeClasses[size]} text-gray-300`} />
                    ))}
                </div>
                <span className="text-sm font-medium text-blue-700 ">
                    {rating.toFixed(1)} {t("myOrdersPage.reviewsCount", { count })}
                </span>
            </div>
        );
    };

    // Calculate professional achievements based on real data and config
    const calculateProfessionalAchievements = () => {
        if (!professionalStats) return [];

        const achievements = [];
        const totalEarned = professionalStats.balance || 0;
        const completedProjects = professionalStats.completedContractsCount || 0;
        const activeProjects = professionalStats.activeContractsCount || 0;
        const totalOffers = professionalStats.totalOffers || 0;
        
        // Calculate average rating from reviews
        const ratingData = userReviews && userReviews.length > 0 
            ? calculateUserAverageRating(userReviews, displayUserId)
            : { average: 0, count: 0 };

        // Get thresholds from config or use defaults
        const config = professionalConfig?.thresholds || {
            topEarnerAmount: 10000,
            expertProjectsCount: 10,
            risingStarProjectsCount: 5,
            risingStarMinRating: 4.5,
            highlyRatedReviewsCount: 10,
            highlyRatedMinRating: 4.7,
        };

        // Top Earner - High earnings
        if (totalEarned >= config.topEarnerAmount) {
            achievements.push({
                title: t("myProfilePage.topEarner"),
                description: t("myProfilePage.topEarnerDesc", { amount: `${getDynamicCurrencySymbol()}${totalEarned.toLocaleString()}` }),
                achieved: true,
                icon: "DollarSign"
            });
        }

        // Expert Professional - High completion count
        if (completedProjects >= config.expertProjectsCount) {
            achievements.push({
                title: t("myProfilePage.expertProfessional"),
                description: t("myProfilePage.expertProfessionalDesc", { count: completedProjects }),
                achieved: true,
                icon: "Award"
            });
        }

        // Rising Star - Good start with ratings
        if (completedProjects >= config.risingStarProjectsCount && ratingData.average >= config.risingStarMinRating) {
            achievements.push({
                title: t("myProfilePage.risingStar"),
                description: t("myProfilePage.risingStarDesc", { count: completedProjects, rating: ratingData.average.toFixed(1) }),
                achieved: true,
                icon: "Star"
            });
        }

        // Highly Rated - Excellent reviews
        if (ratingData.count >= config.highlyRatedReviewsCount && ratingData.average >= config.highlyRatedMinRating) {
            achievements.push({
                title: t("myProfilePage.highlyRated"),
                description: t("myProfilePage.highlyRatedDesc", { count: ratingData.count, rating: ratingData.average.toFixed(1) }),
                achieved: true,
                icon: "Shield"
            });
        }

        // Add pending achievements
        if (totalEarned < config.topEarnerAmount) {
            achievements.push({
                title: t("myProfilePage.topEarner"),
                description: t("myProfilePage.topEarnerPending", { amount: `${getDynamicCurrencySymbol()}${(config.topEarnerAmount - totalEarned).toLocaleString()}` }),
                achieved: false,
                icon: "DollarSign"
            });
        }

        if (completedProjects < config.expertProjectsCount) {
            achievements.push({
                title: t("myProfilePage.expertProfessional"),
                description: t("myProfilePage.expertProfessionalPending", { count: config.expertProjectsCount - completedProjects }),
                achieved: false,
                icon: "Award"
            });
        }

        if (ratingData.count < config.highlyRatedReviewsCount) {
            achievements.push({
                title: t("myProfilePage.highlyRated"),
                description: t("myProfilePage.highlyRatedPending", { count: config.highlyRatedReviewsCount - ratingData.count }),
                achieved: false,
                icon: "Shield"
            });
        }

        return achievements;
    };

    // If admin is editing, use adminUserId, otherwise use normal logic
    const isCurrentUser = isAdminEdit ? true : (!urlUserId || (user && urlUserId === user.id?.toString()));
    const displayUserId = isAdminEdit ? adminUserId : (urlUserId || (user?.id || ""));
    const displayUser = isAdminEdit ? viewedUser : (isCurrentUser ? user : viewedUser); // Determine which user data to display

    // Debug logging
    console.log("MyProfile Debug:", {
        urlUserId,
        user: user?.id,
        displayUserId,
        isCurrentUser,
        viewedUser: viewedUser?.id,
        isAdminEdit,
        adminUserId
    });

    // Helper function to check if user has any contracts with escrows in status 2 or 3
    // where the contract's client ID matches the current logged-in user's ID
    const hasAcceptedEscrows = () => {
        if (!professionalStats?.contracts || !Array.isArray(professionalStats.contracts)) {
            return false;
        }

        const hasAccepted = professionalStats.contracts.some((contract: any) => {
            // Check if contract has escrows array
            if (!contract.escrows || !Array.isArray(contract.escrows)) {
                return false;
            }

            // Check if contract's client ID matches current user's ID
            if (!contract.client?.id || contract.client.id !== user?.id) {
                return false;
            }

            // Check if any escrow has status 2 or 3
            return contract.escrows.some((escrow: any) => {
                const statusId = Number(escrow.status?.id);
                return statusId === 2 || statusId === 3;
            });
        });

        // Debug logging for escrow check
        console.log("Professional Escrow check:", {
            hasAccepted,
            currentUserId: user?.id,
            contractsCount: professionalStats?.contracts?.length || 0,
            contracts: professionalStats?.contracts?.map((c: any) => ({
                id: c.id,
                clientId: c.client?.id,
                escrows: c.escrows?.map((e: any) => ({
                    id: e.id,
                    status: e.status?.id,
                    statusName: e.status?.name
                })) || []
            })) || []
        });

        return hasAccepted;
    };

    // Debug logging for admin mode
    if (isAdminEdit) {
        console.log("Admin Edit Mode Debug:", {
            adminUserId,
            viewedUser: viewedUser?.id,
            user: user?.id,
            displayUser: displayUser?.id,
            isCurrentUser
        });
    }

    // Reset editing states when viewing different profile
    useEffect(() => {
        if (!isCurrentUser) {
            setIsEditing(false);
            setEditingContact(false);
            setEditingCompany(false);
            setEditingLocation(false);
            setIsLanguageEditing(false);
            setEditedProfile(null);
            setShowProfileModal(false);
            setShowAvailabilityModal(false);
        }
    }, [isCurrentUser, urlUserId]);

    // Auto-open profile modal if user has no phone number
    useEffect(() => {
        if (isCurrentUser && user && !user.phone && !isAdminEdit && !isViewing) {
            // Small delay to ensure the component is fully loaded
            const timer = setTimeout(() => {
                setShowProfileModal(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isCurrentUser, user, isAdminEdit, isViewing]);

    // Fetch data for viewed users or admin editing
    useEffect(() => {
        if ((!isCurrentUser && urlUserId) || isAdminEdit) {
            getViewedUserProfileData();
        }
    }, [urlUserId, isCurrentUser, isAdminEdit, adminUserId]);

    // Fetch user categories when profile loads
    useEffect(() => {
        if (displayUserId || isAdminEdit) {
            fetchUserCategoriesForDisplay();
        }
    }, [displayUserId, isAdminEdit, adminUserId]);

    // Fetch user reviews when profile loads
    useEffect(() => {
        if (displayUserId || isAdminEdit) {
            fetchUserReviews();
        }
    }, [displayUserId, isAdminEdit, adminUserId]);

    // Fetch professional stats when profile loads
    useEffect(() => {
        if (displayUserId || isAdminEdit) {
            fetchProfessionalStats();
        }
    }, [displayUserId, isAdminEdit, adminUserId]);

    // Fetch achievement configuration
    useEffect(() => {
        dispatch(getAchievementConfig("professional"));
    }, [dispatch]);

    // Reset editing states when switching between different users in admin mode
    useEffect(() => {
        if (isAdminEdit && adminUserId) {
            // Reset all editing states
            setIsEditing(false);
            setEditingContact(false);
            setEditingCompany(false);
            setEditingLocation(false);
            setIsLanguageEditing(false);
            setEditedProfile(null);
            setShowProfileModal(false);
            setShowAvailabilityModal(false);
        }
    }, [adminUserId, isAdminEdit]);



    const getViewedUserProfileData = async () => {
        const userId = isAdminEdit ? adminUserId : urlUserId;
        if (!userId) return;
        try {
            await dispatch(getUserById({ id: userId }));
        } catch (error) {
            console.error("Error fetching viewed user data:", error);
        }
    };

    const fetchUserReviews = async () => {
        const userId = isAdminEdit ? adminUserId : displayUserId;
        if (!userId) return;
        try {
            await dispatch(fetchReviewsByUser({
                userId: userId,
                query: {
                    page: 1,
                    limit: 100, // Get all reviews for average calculation
                    sort: [{ orderBy: "createdAt", order: "DESC" }]
                }
            }));
        } catch (error) {
            console.error("Error fetching user reviews:", error);
        }
    };

    const fetchProfessionalStats = async () => {
        const userId = isAdminEdit ? adminUserId : displayUserId;
        if (!userId) return;
        try {
            await dispatch(getProfessionalStats({ id: userId, isShowLoader: false }));
        } catch (error) {
            console.error("Error fetching professional stats:", error);
        }
    };

    const fetchUserCategoriesForDisplay = async () => {
        const userId = isAdminEdit ? adminUserId : displayUserId;
        if (!userId) return;
        try {
            // First check for emergency data (type 2)
            const emergencyResponse = await dispatch(getUserCategoriesByType({
                userId: userId,
                type: UserCategoryTypeEnum.Emergency
            })).unwrap();

            // If emergency data exists, use it for display
            if (emergencyResponse && emergencyResponse.categoryIds && emergencyResponse.categoryIds.length > 0) {
                const categories = emergencyResponse.categoryIds || [];
                const subCategories = emergencyResponse.subCategories || [];
                const serviceAreas = emergencyResponse.serviceAreaIds || [];

                console.log("Using emergency data for display - categories:", categories);
                console.log("Using emergency data for display - subCategories:", subCategories);
                console.log("Using emergency data for display - serviceAreas:", serviceAreas);

                setProfileData(prev => ({
                    ...prev,
                    selectedCategories: categories.map(cat => {
                        // Filter subcategories that belong to this category and are selected
                        const selectedSubsForCategory = subCategories.filter(subName =>
                            cat.subcategories && cat.subcategories.includes(subName)
                        );

                        return {
                            id: parseInt(cat.id),
                            name: cat.name,
                            description: cat.description || "",
                            subcategories: selectedSubsForCategory, // Only selected subcategories
                            translatedName: cat.translatedName, // Include translation data
                            translatedSubCategories: cat.translatedSubCategories, // Include subcategory translations
                            isEmergency: cat.isEmergency,
                            isRequireVerification: cat.isRequireVerification,
                            type: cat.type
                        };
                    }),
                    selectedSubcategories: subCategories.map((name) => ({
                        id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                        name,
                        description: ""
                    })),
                    selectedServiceAreas: serviceAreas.map((area: any) => ({
                        id: String(area.id), // Ensure ID is always a string
                        name: area.name || "",
                        province: area.province || "",
                        country: {
                            id: String(area.country?.id || ""), // Ensure country ID is string
                            name: area.country?.name || "",
                            code: area.country?.code || area.country?.initial || ""
                        },
                        type: {
                            id: Number(area.type?.id) || 1, // Ensure type ID is number as expected by interface
                            name: "Default"
                        },
                        radius: Number(area.radius) || 0,
                        restrictions: area.restrictions || [],
                        cities: area.cities || [],
                        postalCodes: area.postalCodes || [],
                        serviceTypes: area.serviceTypes || [],
                        emergencyServices: Boolean(area.emergencyServices) || false
                    }))
                }));
                return; // Exit early since we found emergency data
            }

            // If no emergency data, fall back to profile data (type 1)
            const profileResponse = await dispatch(getUserCategoriesByType({
                userId: userId,
                type: UserCategoryTypeEnum.Profile
            })).unwrap();

            if (profileResponse) {
                const categories = profileResponse.categoryIds || [];
                const subCategories = profileResponse.subCategories || [];
                const serviceAreas = profileResponse.serviceAreaIds || [];

                console.log("Using profile data for display - categories:", categories);
                console.log("Using profile data for display - subCategories:", subCategories);
                console.log("Using profile data for display - serviceAreas:", serviceAreas);

                setProfileData(prev => ({
                    ...prev,
                    selectedCategories: categories.map(cat => {
                        // Filter subcategories that belong to this category and are selected
                        const selectedSubsForCategory = subCategories.filter(subName =>
                            cat.subcategories && cat.subcategories.includes(subName)
                        );

                        return {
                            id: parseInt(cat.id),
                            name: cat.name,
                            description: cat.description || "",
                            subcategories: selectedSubsForCategory, // Only selected subcategories
                            translatedName: cat.translatedName, // Include translation data
                            translatedSubCategories: cat.translatedSubCategories, // Include subcategory translations
                            isEmergency: cat.isEmergency,
                            isRequireVerification: cat.isRequireVerification,
                            type: cat.type
                        };
                    }),
                    selectedSubcategories: subCategories.map((name) => ({
                        id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                        name,
                        description: ""
                    })),
                    selectedServiceAreas: serviceAreas.map((area: any) => ({
                        id: String(area.id), // Ensure ID is always a string
                        name: area.name || "",
                        province: area.province || "",
                        country: {
                            id: String(area.country?.id || ""), // Ensure country ID is string
                            name: area.country?.name || "",
                            code: area.country?.code || area.country?.initial || ""
                        },
                        type: {
                            id: Number(area.type?.id) || 1, // Ensure type ID is number as expected by interface
                            name: "Default"
                        },
                        radius: Number(area.radius) || 0,
                        restrictions: area.restrictions || [],
                        cities: area.cities || [],
                        postalCodes: area.postalCodes || [],
                        serviceTypes: area.serviceTypes || [],
                        emergencyServices: Boolean(area.emergencyServices) || false
                    }))
                }));
            }
        } catch (error) {
            console.error("Error fetching user categories for display:", error);
        }
    };

    // Update profileData when displayUser changes
    useEffect(() => {
        if (displayUser) {
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    name: displayUser?.firstName + " " + displayUser?.lastName,
                    title: displayUser?.title,
                    phone: displayUser?.phone,
                    email: displayUser?.email,
                    taxCode: displayUser?.taxCode || "",
                    photo: displayUser?.photo?.path || prev.personal.photo,
                    hourlyRate: displayUser?.hourlyRate,
                    socialLinks: displayUser?.socialLinks || [],
                    location: displayUser?.address || "",
                    bio: displayUser?.bio,
                },
            }));
        }
    }, [displayUser]);
    console.log("generalSetting", generalSetting);
    // Use languageLevels (new structure with individual levels per language)
    const preferredLanguages = React.useMemo(() => {
        if (!generalSetting) return [];
        
        console.log("=== MyProfile generalSetting ===", generalSetting);
        
        // Use languageLevels array with individual levels per language
        if (generalSetting.languageLevels && generalSetting.languageLevels.length > 0) {
            console.log("✅ Using languageLevels:", generalSetting.languageLevels);
            const mapped = generalSetting.languageLevels.map((langLevel, index) => {
                const languageId = langLevel.language?.id || langLevel.language;
                const level = String(langLevel.level);
                console.log(`Language ${index + 1}:`, { 
                    languageId, 
                    languageName: langLevel.language?.name,
                    level 
                });
                return {
                    id: generalSetting.id,
                    language: languageId,
                    languagePreferences: level,
                };
            });
            console.log("=== Mapped from languageLevels ===", mapped);
            return mapped;
        }
        
        console.log("⚠️ No languageLevels found");
        return [];
    }, [generalSetting]);

    const handleEmergencyHoursClick = () => {
        // Check if emergency hours already exist
        if (Number(user?.role?.id) === RoleEnum.internationl_professional) {
            GlobalService.showToast(
                "info",
                t("myProfilePage.information"),
                t("myProfilePage.internationalProfessionalRestriction")
            );
            return;
        }
        if (emergencyAvailabilities && emergencyAvailabilities.length > 0) {
            // If emergency hours exist, directly open the availability modal
            setShowAvailabilityModal(true);
        } else {
            // If no emergency hours exist, show the confirmation modal first
            setShowEmergencyHoursModal(true);
        }
    };

    const handleEmergencyHoursConfirm = () => {
        setShowEmergencyHoursModal(false);
        setShowAvailabilityModal(true);
    };

    const handleUnauthenticatedAction = () => {
        // Store the current URL for redirect after login/register
        if (!user?.id) {
            localStorage.setItem("redirectAfterLogin", window.location.pathname);
            navigate("/auth/register");
        }
        else {
            // Check if user is a client and authenticated
            if (Number(user?.role?.id) === RoleEnum.client) {
                // Show contract milestone modal with offers
                handleShowContractModal();
            } else {
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                navigate(`/client/MessageAndVideoCall?professionalId=${targetUserId}}`);
            }
        }
    };

    const handleShowContractModal = async () => {
        setLoadingOffers(true);
        try {
            // Fetch client offers
            const response = await dispatch(fetchUserOffers({
                page: 1,
                limit: 20, // Get all offers
                jobUserId: user.id,
                isSent: false, // Get offers received by the client
                statusIds: "2,3", // Approved and Accepted offers
                sort: [{ orderBy: "createdAt", order: "DESC" }]
            }));
            
            if (response.meta.requestStatus === "fulfilled") {
                setClientOffers((response.payload as any)?.data || []);
                setShowContractMilestoneModal(true);
            }
        } catch (error) {
            console.error("Error fetching client offers:", error);
        } finally {
            setLoadingOffers(false);
        }
    };

    const handleUnauthenticatedContact = () => {
        // Store the current URL for redirect after login/register
        localStorage.setItem("redirectAfterLogin", window.location.pathname);
        navigate("/auth/register");
    };

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        } finally {
            navigate("/");
        }
    };

    const sidebarItems = [
        { icon: <Home className="h-5 w-5" />, label: t("myProfilePage.dashboard"), href: "/professionals" },
        { icon: <User className="h-5 w-5" />, label: t("myProfilePage.myProfile"), href: "/myprofile", active: true },
        { icon: <Settings className="h-5 w-5" />, label: t("myProfilePage.settings"), href: "/Settings" },
        { icon: <UserSearchIcon className="h-5 w-5" />, label: t("sidebar.localCustomers"), href: "/professional" },
        {
            icon: <Bookmark className="h-5 w-5" />,
            label: t("sidebar.internationalClients"),
            href: "/international-professionals",
        },
        { icon: <FileText className="h-5 w-5" />, label: t("myProfilePage.myOrders"), href: "/myorders" },
        { icon: <ChartAreaIcon className="h-5 w-5" />, label: t("myProfilePage.progress"), href: "/Progress" },
        { icon: <MessageSquare className="h-5 w-5" />, label: t("myProfilePage.messages"), href: "/MessageAndVideoCall" },
        { icon: <Shield className="h-5 w-5" />, label: t("myProfilePage.supportTickets"), href: "/support" },
        {
            icon: <Clock className="h-5 w-5" />,
            label: t("myProfilePage.emergencyHours"),
            onClick: handleEmergencyHoursClick,
        },
        { icon: <LogOut className="h-5 w-5" />, label: t("myProfilePage.logOut"), href: "/login" },
    ];
    const [editingLanguages, setEditingLanguages] = useState([]);
    const [showImportantInfoModal, setShowImportantInfoModal] = useState(false);
    const [showEmergencyHoursModal, setShowEmergencyHoursModal] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [validationPersonalErrors, setValidationPersonalErrors] = useState<ValidationPersonalErrors>({
        personal: {},
    });
    const [validationCompanyErrors, setValidationCompanyErrors] = useState<ValidationCompanyErrors>({
        company: {},
    });
    
    // Contract milestone modal state
    const [showContractMilestoneModal, setShowContractMilestoneModal] = useState(false);
    const [clientOffers, setClientOffers] = useState([]);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [loadingOffers, setLoadingOffers] = useState(false);

    // Format time ago utility function - declared before useState to use in initial state
    const formatTimeAgo = (timestamp: string | Date | undefined): string => {
        if (!timestamp) return t("myProfilePage.currentlyActive") || "Currently active";

        const now = new Date();
        const date = new Date(timestamp);
        const diffInMs = now.getTime() - date.getTime();
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

        if (diffInDays > 0) {
            return t("myProfilePage.daysAgo", { count: diffInDays });
        } else if (diffInHours > 0) {
            return t("myProfilePage.hoursAgo", { count: diffInHours });
        } else if (diffInMinutes > 0) {
            return t("myProfilePage.minutesAgo", { count: diffInMinutes });
        } else {
            return t("myProfilePage.currentlyActive") || "Just now";
        }
    };

    const [profileData, setProfileData] = useState({
        serviceCategories: ["Web Development", "Mobile Development", "Cloud Architecture"],
        selectedCategories: [],
        selectedSubcategories: [],
        selectedServiceAreas: [],
        reviews: [
            {
                id: 1,
                clientName: "Sarah Johnson",
                clientImage: "https://i.pravatar.cc/150?img=1",
                clientCompany: "Tech Solutions Inc.",
                rating: 5,
                projectTitle: "Complete Website Redesign",
                date: "October 2023",
                projectBudget: "$5,000",
                projectDuration: "2 months",
                review: "Exceptional work! The attention to detail and commitment to quality was outstanding.",
                skills: ["Web Design", "React", "UI/UX"],
                verifiedHire: true,
            },
        ],
        badges: { topRatedPlus: true, topRated: true, risingTalent: false, expertVerified: true },
        stats: { totalJobs: 186, inProgress: 3, completionRate: 98, onBudget: 95, onTime: 97, rehireRate: 85 },
        personal: {
            name: (displayUser?.firstName || "") + " " + (displayUser?.lastName || ""),
            title: displayUser?.title || "",
            phone: displayUser?.phone || "",
            email: displayUser?.email || "",
            taxCode: displayUser?.taxCode || "",
            timezone: "UTC-5",
            photo:
                displayUser?.photo?.path,
            hourlyRate: displayUser?.hourlyRate || 0,
            socialLinks: displayUser?.socialLinks || [],
            company: {
                name: "",
                industry: "",
                size: "",
                founded: "",
                website: "",
                description: "",
            },
            location: displayUser?.address || "",
            bio: displayUser?.bio || "",
            languages: ["English (Native)", "French (Professional)", "Spanish (Conversational)"],
            joined: (() => {
                const currentUserData = displayUser || user;
                if (!currentUserData?.createdAt) {
                    const defaultDate = new Date("2023-01-01");
                    const options: Intl.DateTimeFormatOptions = {
                        year: "numeric",
                        month: "long",
                    };
                    const lang = i18n.language?.split("-")[0] || "en";
                    return defaultDate.toLocaleDateString(lang, options);
                }
                const date = new Date(currentUserData.createdAt);
                const options: Intl.DateTimeFormatOptions = {
                    year: "numeric",
                    month: "long",
                };
                const lang = i18n.language?.split("-")[0] || "en";
                return date.toLocaleDateString(lang, options);
            })(),
            lastActive: formatTimeAgo((displayUser || user)?.updatedAt || (displayUser || user)?.createdAt),
            verified: true,
            rating: 4.9,
            completedProjects: 0,
            totalEarned: "0K+",
            successRate: 0,
        },
        privacy: { profileVisibility: "public", showEmail: false, showPhone: false },
        notifications: { newMessages: true, projectUpdates: true, newsletters: false, marketing: false },
        security: { twoFactorEnabled: false, lastPasswordChange: "2024-01-15" },
    });

    const handlePersonalInfoEdit = () => {
        setShowImportantInfoModal(true);
    };

    const handleUnderstandClick = () => {
        setShowImportantInfoModal(false);
        setShowProfileModal(true);
    };
    const handlePhotoUpload = async e => {
        if (!user || !isCurrentUser) return; // Only allow current user to upload photos
        const file = e.target.files?.[0];

        if (!file) return;

        // Create a preview URL for immediate display
        const previewUrl = URL.createObjectURL(file);
        setImageFile(previewUrl);

        // Update local state immediately for real-time preview
        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                photo: previewUrl,
            },
        }));

        try {
            const { payload } = await dispatch(uploadUserImage({ file }));

            if (payload?.file?.path) {
                // Update with the actual uploaded file path
                setProfileData(prev => ({
                    ...prev,
                    personal: {
                        ...prev.personal,
                        photo: payload.file.path,
                    },
                }));

                // Clean up the preview URL
                URL.revokeObjectURL(previewUrl);
                setImageFile(null);

                // Update user profile in backend
                const updateImageDataInBackground = {
                    ...user,
                    photo: {
                        id: payload.file.id,
                        path: payload.file.path,
                    },
                };
                // Remove email from payload to avoid "emailAlreadyExists" error
                delete updateImageDataInBackground.email;

                const targetUserId = isAdminEdit ? adminUserId : user.id;
                await dispatch(UpdateUserProfile({ id: targetUserId, data: updateImageDataInBackground }));

                // Call admin callback if provided
                if (isAdminEdit && onUserUpdated) {
                    onUserUpdated();
                }
            }
        } catch (error) {
            console.error("Error uploading photo:", error);
            // Revert to original photo on error
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    photo: user?.photo?.path || "",
                },
            }));
            setImageFile(null);
            URL.revokeObjectURL(previewUrl);
            GlobalService.showToast("error", t("toast.titles.error"), t("myProfilePage.photoUploadFailed"));
        }
    };

    const handlePhotoRemove = async () => {
        if (!user || !isCurrentUser) return; // Only allow current user to remove photos
        try {
            // Clear the imageFile state first
            setImageFile(null);

            // Clear the photo from local state immediately for real-time update
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    photo: "",
                },
            }));

            // Update the user profile in the backend with null photo
            const updateImageDataInBackground = {
                ...user,
                photo: null, // Set photo to null to remove it
            };
            // Remove email from payload to avoid "emailAlreadyExists" error
            delete updateImageDataInBackground.email;

            const targetUserId = isAdminEdit ? adminUserId : user.id;
            await dispatch(UpdateUserProfile({ id: targetUserId, data: updateImageDataInBackground }));

            GlobalService.showToast("success", t("toast.titles.success"), t("myProfilePage.photoRemovedSuccess"));

            // Call admin callback if provided
            if (isAdminEdit && onUserUpdated) {
                onUserUpdated();
            }
        } catch (error) {
            console.error("Error removing photo:", error);
            GlobalService.showToast("error", t("toast.titles.error"), t("myProfilePage.photoRemoveFailed"));
        }
    };

    const handleShareProfile = async () => {
        if (!displayUserId) {
            GlobalService.showToast("error", t("toast.titles.error"), t("myProfilePage.unableToGenerateURL"));
            return;
        }
        const profileUrl = `${window.location.origin}/myprofile/${displayUserId}`;
        try {
            await navigator.clipboard.writeText(profileUrl);
            GlobalService.showToast("success", t("toast.titles.success"), t("myProfilePage.profileURLCopied"));
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = profileUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            GlobalService.showToast("success", t("toast.titles.success"), t("myProfilePage.profileURLCopied"));
        }
    };
    const startEditingLocation = () => {
        if (!editedProfile) {
            setEditedProfile({ ...profileData.personal });
        }
        setEditingLocation(true);
    };
    const handleFieldChange = (field, value) => {
        setEditedProfile(prev => ({ ...prev, [field]: value }));
    };

    // Function to extract country code from VAT number
    const extractCountryCodeFromVAT = (vatNumber: string): string => {
        if (!vatNumber || vatNumber.length < 2) return "";

        // VAT numbers typically start with 2 letters (country code)
        const countryCode = vatNumber.substring(0, 2).toUpperCase();

        // Validate that it's actually letters
        if (/^[A-Z]{2}$/.test(countryCode)) {
            return countryCode;
        }

        return "";
    };
    const handleCompanySaveChanges = async () => {
        if (!isCurrentUser) return; // Only allow current user to update company info

        // Reset validation errors
        setValidationCompanyErrors({
            company: {},
        });

        let hasErrors = false;
        const newValidationErrors: ValidationCompanyErrors = {
            company: {},
        };
        if (!editedProfile) return;
        // Track which sections have changes
        let hasCompanyChanges = false;

        // Company info validation
        if (editedProfile.company) {
            const companyWasEmpty =
                !profileData.personal.company ||
                (!profileData.personal.company.name &&
                    !profileData.personal.company.description &&
                    !profileData.personal.company.founded &&
                    !profileData.personal.company.website &&
                    !profileData.personal.company.industry &&
                    !profileData.personal.company.size);

            // Only require validation if:
            // 1. Company wasn't empty originally OR
            // 2. User has modified at least one company field
            const isVatOnlyUpdate = (
                (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber)
                && !editedProfile.company.name
                && !editedProfile.company.description
                && !editedProfile.company.founded
                && !editedProfile.company.website
                && !editedProfile.company.industry
                && !editedProfile.company.size
            );

            const shouldValidateCompany =
                (!companyWasEmpty || (
                    editedProfile.company.name ||
                    editedProfile.company.description ||
                    editedProfile.company.founded ||
                    editedProfile.company.website ||
                    editedProfile.company.industry ||
                    editedProfile.company.size
                )) && !isVatOnlyUpdate;

            if (shouldValidateCompany) {
                if (!editedProfile.company.name) {
                    newValidationErrors.company.name = "Company name is required";
                    hasErrors = true;
                } else if (editedProfile.company.name !== profileData.personal.company.name) {
                    hasCompanyChanges = true;
                }

                if (!editedProfile.company.description) {
                    newValidationErrors.company.description = "Company description is required";
                    hasErrors = true;
                } else if (editedProfile.company.description !== profileData.personal.company.description) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.founded) {
                    newValidationErrors.company.founded = "Founded year is required";
                    hasErrors = true;
                } else if (!/^\d{4}$/.test(editedProfile.company.founded)) {
                    newValidationErrors.company.founded = "Must be a valid year (YYYY)";
                    hasErrors = true;
                } else if (parseInt(editedProfile.company.founded) > new Date().getFullYear()) {
                    newValidationErrors.company.founded = "Year cannot be in the future";
                    hasErrors = true;
                } else if (editedProfile.company.founded !== profileData.personal.company.founded) {
                    hasCompanyChanges = true;
                }
                // Website optional; only mark change when different
                if (editedProfile.company.website !== profileData.personal.company.website) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.industry) {
                    newValidationErrors.company.industry = "Industry is required";
                    hasErrors = true;
                } else if (editedProfile.company.industry !== profileData.personal.company.industry) {
                    hasCompanyChanges = true;
                }
                if (!editedProfile.company.size) {
                    newValidationErrors.company.companySize = "Company size is required";
                    hasErrors = true;
                } else if (editedProfile.company.size !== profileData.personal.company.size) {
                    hasCompanyChanges = true;
                }

                // Check for VAT number changes
                if (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber) {
                    hasCompanyChanges = true;
                }

                // Check for legal status changes
                if (editedProfile.company.legalStatus !== (profileData.personal.company as any)?.legalStatus) {
                    hasCompanyChanges = true;
                }
            } else {
                // If VAT-only update, still mark changes when value changed
                if (editedProfile.company.vatNumber !== (profileData.personal.company as any)?.vatNumber) {
                    hasCompanyChanges = true;
                }
            }
        }
        if (hasErrors) {
            setValidationCompanyErrors(newValidationErrors);
            return;
        }

        // Require vatNumber for company save
        if (!editedProfile.company?.vatNumber || editedProfile.company.vatNumber.trim() === "") {
            (newValidationErrors.company as any).vatNumber = "VAT number is required";
            setValidationCompanyErrors(newValidationErrors);
            return;
        }

        // ID validation logic (only verify via VIES when selected)
        let isVatValid = false;
        const selectedIdType = editedProfile.company?.idType;
        const shouldVerifyVies = selectedIdType === "vatNumberVies";
        if (shouldVerifyVies && editedProfile.company?.vatNumber) {
            // Extract country code from the VAT number
            const extractedCountryCode = extractCountryCodeFromVAT(editedProfile.company.vatNumber);

            if (!extractedCountryCode) {
                GlobalService.showToast("error", "VAT Validation", "Invalid VAT number format. VAT number should start with a 2-letter country code (e.g., CZ03571786)");
                return;
            }

            // Extract VAT number without country code
            const vatNumberWithoutCountryCode = editedProfile.company.vatNumber.substring(2);

            dispatch(showLoader());
            try {
                // Validate VAT number using VIES API
                const vatValidationResponse = await dispatch(validateVATNumber({
                    countryCode: extractedCountryCode,
                    vatNumber: vatNumberWithoutCountryCode,
                    companyId: company?.id
                })).unwrap();
                isVatValid = vatValidationResponse.valid;

                if (vatValidationResponse.valid) {
                    GlobalService.showToast("success", "VAT Validation", "VAT number is valid");
                } else {
                    GlobalService.showToast("error", "VAT Validation", "VAT number is invalid");
                    // return;
                }
            } catch (error) {
                console.error("VAT validation error:", error);
                GlobalService.showToast("error", "VAT Validation", "Failed to validate VAT number");
                return;
            }
        } else if (company) {
            // Preserve existing VAT validation status if present
            isVatValid = company.isVatValid || false;
        }

        // Only proceed if no validation errors
        setProfileData(prev => ({ ...prev, personal: { ...editedProfile } }));
        setEditingCompany(false);

        try {
            // Only update company if company info changed
            if (hasCompanyChanges && editedProfile.company) {
                const targetUserId = isAdminEdit ? adminUserId : user?.id;
                const basePayload = {
                    name: editedProfile.company.name,
                    industry: { id: Number(editedProfile.company.industry) },
                    companySize: { id: Number(editedProfile.company.size) },
                    year: parseInt(editedProfile.company.founded, 10),
                    website: editedProfile.company.website,
                    description: editedProfile.company.description,
                    vatNumber: editedProfile.company.vatNumber || "",
                    legalStatus: { id: Number(editedProfile.company.legalStatus) || 1 },
                    isVatValid,
                    userId: { id: targetUserId },
                };

                if (company == null) {
                    const createPayload = {
                        ...basePayload,
                        country: "",
                        postalCode: "",
                        state: "",
                        city: "",
                        address: "",
                        phone: "",
                        email: "",
                        taxId: "",
                        registrationNumber: "",
                        trading: "",
                    };
                    await dispatch(createUserCompany({ data: createPayload }));
                } else {
                    await dispatch(updateUserCompanies({ id: company?.id, data: basePayload }));
                }
            }
        } catch (error) {
            console.error("Error saving changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    const handleContactSaveChanges = async () => {
        if (!isCurrentUser) return; // Only allow current user to update contact info

        // Reset validation errors
        setValidationPersonalErrors({
            personal: {},
        });
        setValidationCompanyErrors({
            company: {},
        });

        let hasErrors = false;
        const newValidationPersonalErrors: ValidationPersonalErrors = {
            personal: {},
        };
        const newValidationCompanyErrors: ValidationCompanyErrors = {
            company: {},
        };

        if (!editedProfile) return;

        // Track changes
        let hasSocialLinksChanges = false;
        let hasWebsiteChanges = false;

        // Check if social links have changed
        const originalLinks = profileData.personal.socialLinks || [];
        const currentLinks = editedProfile.socialLinks || [];

        if (originalLinks.length !== currentLinks.length) {
            hasSocialLinksChanges = true;
        } else if (originalLinks.some((link, index) => link !== currentLinks[index])) {
            hasSocialLinksChanges = true;
        }

        // Check if website has changed
        const originalWebsite = profileData.personal.company?.website || "";
        const currentWebsite = editedProfile.company?.website || "";
        if (currentWebsite !== originalWebsite) {
            hasWebsiteChanges = true;
        }
        // Validate social links only if they exist in the form
        if (currentLinks.length > 0 || hasSocialLinksChanges) {
            if (currentLinks.length === 0 || currentLinks.every(link => !link)) {
                newValidationPersonalErrors.personal.socialLinks = "At least one social link is required";
                hasErrors = true;
            }
        }

        // Validate website only if it's being modified or was previously empty and user is trying to add it
        if (hasWebsiteChanges || (!originalWebsite && currentWebsite)) {
            if (!currentWebsite) {
                newValidationCompanyErrors.company.website = "Website is required";
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setValidationPersonalErrors(newValidationPersonalErrors);
            setValidationCompanyErrors(newValidationCompanyErrors);
            return;
        }

        try {
            // Update social links if changed
            if (hasSocialLinksChanges) {
                const payloadUser = {
                    ...user,
                    socialLinks: editedProfile.socialLinks.filter(link => link), // remove empty links
                };
                // Remove email from payload to avoid "emailAlreadyExists" error
                delete payloadUser.email;
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                await dispatch(UpdateUserProfile({ id: targetUserId, data: payloadUser }));
            }

            // Handle company website changes only if there are changes
            if (hasWebsiteChanges && editedProfile.company) {
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                const basePayload = {
                    name: "",
                    industry: { id: 1 },
                    companySize: { id: 1 },
                    year: 0,
                    website: editedProfile.company.website,
                    description: "",
                    userId: { id: targetUserId },
                };
                if (!company) {
                    const createPayload = {
                        ...basePayload,
                        country: "",
                        legalStatus: { id: 1 },
                        postalCode: "",
                        state: "",
                        city: "",
                        address: "",
                        phone: "",
                        email: "",
                        taxId: "",
                        vatNumber: "",
                        registrationNumber: "",
                        trading: "",
                        userId: { id: targetUserId },
                    };
                    await dispatch(createUserCompany({ data: createPayload }));
                } else {
                    await dispatch(updateUserCompanies({ id: company.id, data: basePayload }));
                }
            }

            // Update local state
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    socialLinks: editedProfile.socialLinks.filter(link => link), // remove empty links
                    company: {
                        ...prev.personal.company,
                        website: editedProfile.company?.website || prev.personal.company?.website || "",
                    },
                },
            }));

            setEditingContact(false);
        } catch (error) {
            console.error("Error saving contact changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };

    const handleLocationSaveChanges = async () => {
        if (!isCurrentUser) return; // Only allow current user to update location

        // Reset validation errors
        setValidationPersonalErrors({
            personal: {},
        });

        let hasErrors = false;

        const newValidationPersonalErrors: ValidationPersonalErrors = {
            personal: {},
        };
        if (!editedProfile) return;

        // Track changes
        let hasLocationChanges = false;

        // Personal info validation - check for either location or address
        if (!editedProfile?.location && !editedProfile?.address) {
            newValidationPersonalErrors.personal.location = "Location is required";
            hasErrors = true;
        } else if (
            editedProfile.location !== profileData.personal.location ||
            editedProfile.address !== (profileData.personal as any).address ||
            editedProfile.latitude !== (profileData.personal as any).latitude ||
            editedProfile.longitude !== (profileData.personal as any).longitude
        ) {
            hasLocationChanges = true;
        }
        if (hasErrors) {
            setValidationPersonalErrors(newValidationPersonalErrors);
            return;
        }

        try {
            // Handle location changes only if there are changes
            if (hasLocationChanges) {
                const payloadUser: any = {
                    ...user,
                    address: editedProfile.address || editedProfile.location,
                    latitude: editedProfile.latitude,
                    longitude: editedProfile.longitude,
                    location: editedProfile.location || editedProfile.address,
                };
                // Include province and country in user payload if available
                if (editedProfile.province) {
                    payloadUser.province = editedProfile.province;
                }
                if (editedProfile.country && editedProfile.country.id) {
                    payloadUser.country = { id: editedProfile.country.id };
                }
                const targetUserId = isAdminEdit ? adminUserId : user.id;
                await dispatch(UpdateUserProfile({ id: targetUserId, data: payloadUser }));
            }

            // Update local state
            setProfileData(prev => ({
                ...prev,
                personal: {
                    ...prev.personal,
                    location: editedProfile?.location || editedProfile?.address,
                    address: editedProfile?.address,
                    latitude: editedProfile?.latitude,
                    longitude: editedProfile?.longitude,
                },
            }));

            setEditingLocation(false);
        } catch (error) {
            console.error("Error saving location changes:", error);
        }

        // Call admin callback if provided
        if (isAdminEdit && onUserUpdated) {
            onUserUpdated();
        }
    };
    useEffect(() => {
        getAllFieldsValue();
    }, [displayUserId, isAdminEdit]);

    const getAllFieldsValue = async () => {
        const userId = isAdminEdit ? adminUserId : displayUserId;
        if (!userId) return;

        dispatch(showLoader());
        await dispatch(getUserCompanies({ id: userId, isShowLoader: false }));
        await dispatch(getAllLanguages({ isShowLoader: false }));
        await dispatch(getAllCurrencies({ isShowLoader: false }));
        await dispatch(getAllTimezones({ isShowLoader: false }));
        await dispatch(getUserGeneralSettingAll({ id: userId, isShowLoader: false }));
        await dispatch(getUserGeneralSetting({ id: userId, isShowLoader: false }));
        dispatch(hideLoader());
    };

    useEffect(() => {
        const formatJoinedDate = (dateString: string) => {
            if (!dateString) {
                // Return default in current language
                const defaultDate = new Date("2023-01-01");
                const options: Intl.DateTimeFormatOptions = {
                    year: "numeric",
                    month: "long",
                };
                const lang = i18n.language?.split("-")[0] || "en";
                return defaultDate.toLocaleDateString(lang, options);
            }

            const date = new Date(dateString);
            const options: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "long",
            };
            // Use current language from i18n
            const lang = i18n.language?.split("-")[0] || "en";
            return date.toLocaleDateString(lang, options);
        };

        // Use displayUser instead of user to show the correct profile data
        const currentUserData = displayUser || user;

        setProfileData(prev => ({
            ...prev,
            personal: {
                ...prev.personal,
                ...(currentUserData && {
                    name: `${currentUserData.firstName || ""} ${currentUserData.lastName || ""}`,
                    title: currentUserData.title || "",
                    photo: currentUserData?.photo?.path || "",
                    email: currentUserData.email || "",
                    phone: currentUserData.phone || "",
                    cover:
                        currentUserData?.cover?.path ||
                        "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3",
                    bio: currentUserData?.bio || prev.personal.bio,
                    socialLinks: currentUserData?.socialLinks || [],
                    location: currentUserData?.address || "",
                    joined: formatJoinedDate(currentUserData.createdAt),
                    lastActive: formatTimeAgo(currentUserData.updatedAt || currentUserData.createdAt),
                }),
                // Only show company data if viewing current user's profile or if we have company data for the viewed user
                ...(company && (isCurrentUser || company.userId?.id === displayUserId) && {
                    company: {
                        name: company.name || "",
                        industry: company.industry?.id || "",
                        size: company.companySize?.id || "",
                        founded: company.year ? company.year.toString() : "",
                        website: company.website || "",
                        description: company.description || "",
                        vatNumber: company.vatNumber || "",
                        legalStatus: company.legalStatus?.id || "",
                        isVatValid: company.isVatValid || false,
                    },
                }),
            },
        }));
    }, [displayUser, user, company, isAdminEdit, adminUserId, viewedUser, displayUserId, t, i18n.language]);
    const startEditingContact = () => {
        if (!editedProfile) {
            setEditedProfile({ ...profileData.personal });
        }
        setEditingContact(true);
    };
    const handleCompanyFieldChange = (companyField, value) => {
        setEditedProfile(prev => ({
            ...prev,
            company: { ...prev.company, [companyField]: value },
        }));
    };

    const handleSocialLinkChange = (index, value) => {
        setEditedProfile(prev => {
            if (!prev) return prev;
            const updatedSocialLinks = prev.socialLinks ? [...prev.socialLinks] : [];
            updatedSocialLinks[index] = value;
            return {
                ...prev,
                socialLinks: updatedSocialLinks,
            };
        });
    };
    const startEditingLanguage = () => {
        // Start with current languages or empty array if none exist
        setEditingLanguages(
            preferredLanguages.length > 0 ? [...preferredLanguages] : [{ language: "", languagePreferences: "" }]
        );
        setIsLanguageEditing(true);
    };
    const handleCancelEdit = () => {
        setEditingCompany(false);
    };
    const startEditingCompany = () => {
        if (!editedProfile) {
            setEditedProfile({ ...profileData.personal });
        }
        setEditingCompany(true);
    };

    const handleEmergencyHoursSaved = async () => {
        // Reset regular hours when emergency hours are saved
        const userId = isAdminEdit ? adminUserId : displayUserId;
        if (userId) {
            try {
                // Get current availabilities from Redux state
                // const { availabilities } = useSelector((state: any) => state.professional); // This line is removed

                // Clear regular availability hours by setting them to inactive
                const resetPayload = {
                    isEmergency: false,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    userId: { id: userId },
                    startAt: new Date().toISOString(),
                    endAt: new Date().toISOString(),
                    isAvailable: false,
                    day: "Monday", // This will be updated for each day
                };

                // Get current regular availabilities and set them to inactive
                if (availabilities && availabilities.length > 0) {
                    const regularAvailabilities = availabilities.filter(av => !av.isEmergency);
                    for (const availability of regularAvailabilities) {
                        await dispatch(UpdateUserAvailabilities({
                            id: availability.id,
                            data: { ...resetPayload, day: availability.day }
                        }));
                    }
                }

                // Refresh the data
                await dispatch(getUserAvailabilities({ id: userId }));

                // Refresh user categories data to show emergency data
                await fetchUserCategoriesForDisplay();

                console.log("Regular hours reset successfully");
            } catch (error) {
                console.error("Error resetting regular hours:", error);
            }
        }
    };

    // Show loading if we don't have the necessary data
    if (!displayUser && !isCurrentUser && !isAdminEdit) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Spinner size={8} />
                    <p className="mt-4 text-gray-600">{t("myProfilePage.loadingProfile")}</p>
                </div>
            </div>
        );
    }

    // Show loading in admin mode if viewedUser is not loaded yet
    if (isAdminEdit && !viewedUser) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Spinner size={8} />
                    <p className="mt-4 text-gray-600">{t("myProfilePage.loadingUserProfile")}</p>
                </div>
            </div>
        );
    }

    return (
        <OrientationLock enabled={true}>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                {/* Fixed Header */}
                {!isAdminEdit && (
                    <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-14 sm:h-16">
                        <SharedHeader4 />
                    </div>
                )}

                <div className={`flex min-h-[calc(100vh-56px)] sm:min-h-[calc(100vh-64px)] ${isAdminEdit ? "" : "pt-14 sm:pt-16"}`}>
                    {/* Desktop Sidebar - Only show for current user and not in admin edit mode */}
                    {isCurrentUser && !isAdminEdit && (
                        <aside className="hidden md:block fixed left-0 top-14 sm:top-16 bottom-0 w-56 xl:w-64 bg-white border-r shadow-sm overflow-y-auto">
                            <nav className="p-3 sm:p-4 space-y-1">
                                {sidebarItems.map((item, index) => (
                                    item.href == "/login" ?
                                        <button
                                            key={index}
                                            onClick={() => handleLogout()}
                                            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm transition-colors w-full text-left ${item.active
                                                ? "text-blue-600 bg-blue-50 font-medium"
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}>
                                            <span className="flex-shrink-0">{item.icon}</span>
                                            <span className="truncate">{item.label}</span>
                                        </button>
                                        : <button
                                            key={index}
                                            onClick={() => {
                                                if (item.href) {
                                                    window.location.href = item.href;
                                                } else if (item.onClick) {
                                                    item.onClick();
                                                }
                                            }}
                                            className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm transition-colors w-full text-left ${item.active
                                                ? "text-blue-600 bg-blue-50 font-medium"
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}>
                                            <span className="flex-shrink-0">{item.icon}</span>
                                            <span className="truncate">{item.label}</span>
                                        </button>
                                ))}
                            </nav>
                        </aside>
                    )}

                    {/* Mobile Sidebar - Only show for current user and not in admin edit mode */}
                    {isCurrentUser && !isAdminEdit && (
                        <AnimatePresence>
                            {isMobileMenuOpen && (
                                <>
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
                                    />
                                    <motion.aside
                                        initial={{ x: "-100%" }}
                                        animate={{ x: 0 }}
                                        exit={{ x: "-100%" }}
                                        transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                                        className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 md:hidden overflow-y-auto">
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-6">
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src="https://assests.netlify.app/assets/images/logo.png"
                                                        alt="Logo"
                                                        className="h-8 w-8"
                                                    />
                                                    <span className="text-blue-600 text-lg font-bold">Kokobeo</span>
                                                </div>
                                                <button
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                                                    <X className="h-6 w-6" />
                                                </button>
                                            </div>
                                            <nav className="space-y-1">
                                                {sidebarItems.map((item, index) => (
                                                    item.href == "/login" ?
                                                        <button
                                                            key={index}
                                                            onClick={() => {
                                                                handleLogout();
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors w-full text-left ${item.active
                                                                ? "text-blue-600 bg-blue-50 font-medium"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                                }`}>
                                                            {item.icon}
                                                            <span>{item.label}</span>
                                                        </button>
                                                        : <button
                                                            key={index}
                                                            onClick={() => {
                                                                if (item.href) {
                                                                    window.location.href = item.href;
                                                                } else if (item.onClick) {
                                                                    item.onClick();
                                                                }
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors w-full text-left ${item.active
                                                                ? "text-blue-600 bg-blue-50 font-medium"
                                                                : "text-gray-600 hover:bg-gray-50"
                                                                }`}>
                                                            {item.icon}
                                                            <span>{item.label}</span>
                                                        </button>
                                                ))}
                                            </nav>
                                        </div>
                                    </motion.aside>
                                </>
                            )}
                        </AnimatePresence>
                    )}

                    {/* Main Content */}
                    <main className={`flex-1 px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 max-w-6xl mx-auto ${isCurrentUser && !isAdminEdit ? "md:pl-56 xl:pl-64" : ""}`}>
                        <div className="space-y-4 sm:space-y-6">
                            {/* Profile Header */}
                            <Card color={undefined} className="p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-start">
                                    <div className="relative mx-auto sm:mx-0">
                                        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                                            {(() => {
                                                const photoUrl = imageFile || profileData.personal.photo;
                                                return photoUrl && photoUrl.trim() !== "" ? (
                                                    <img
                                                        src={photoUrl}
                                                        alt={profileData.personal.name}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            // If the profile photo fails to load, hide the img and show User icon
                                                            e.currentTarget.style.display = "none";
                                                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                            if (nextElement) {
                                                                nextElement.style.display = "flex";
                                                            }
                                                        }}
                                                    />
                                                ) : null;
                                            })()}
                                            <div className={`w-full h-full flex items-center justify-center ${(() => {
                                                const photoUrl = imageFile || profileData.personal.photo;
                                                return photoUrl && photoUrl.trim() !== "" ? "hidden" : "flex";
                                            })()}`}>
                                                <User className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 text-gray-400" />
                                            </div>
                                            {isCurrentUser && !isViewing && !urlUserId && (
                                                <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 flex gap-1">
                                                    <label
                                                        htmlFor="profile-image-upload"
                                                        className="p-1.5 sm:p-2 bg-blue-600 rounded-full text-white hover:bg-blue-700 cursor-pointer">
                                                        <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                                                        <input
                                                            id="profile-image-upload"
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={handlePhotoUpload}
                                                        />
                                                    </label>
                                                    {(() => {
                                                        const photoUrl = imageFile || profileData.personal.photo;
                                                        return photoUrl && photoUrl.trim() !== "" ? (
                                                            <button
                                                                        onClick={handlePhotoRemove}
                                                                        className="p-1.5 sm:p-2 bg-red-600 rounded-full text-white hover:bg-red-700 cursor-pointer"
                                                                        title={t("myProfilePage.removePhoto")}
                                                                    >
                                                                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                                                    </button>
                                                        ) : null;
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        {profileData.personal.verified && (
                                            <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-green-500 text-white p-1 sm:p-1.5 rounded-full">
                                                <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
                                            </div>
                                        )}
                                        {/* Favorite Button - Only show when viewing someone else's profile */}
                                        {/* Moved to below timezone section */}
                                    </div>
                                    <div className="flex-1 w-full sm:w-auto">
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                                            <div className="text-center sm:text-left">
                                                <div className="flex justify-center sm:justify-start">
                                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                                                        {profileData.personal.name}
                                                        {profileData.personal.verified && (
                                                            <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                                                        )}
                                                    </h1>
                                                </div>

                                                <p className="text-base sm:text-lg text-gray-600">{profileData.personal.title}</p>
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        <MapPin className="w-4 h-4" />
                                                        {Number((displayUser || user)?.role?.id) === RoleEnum.internationl_professional && (profileData.selectedServiceAreas?.length || 0) > 0
                                                            ? profileData.selectedServiceAreas
                                                                .slice(0, 2)
                                                                .map((a: any) => (a?.province ? `${a.province}, ${a?.country?.name || ""}` : (a?.country?.name || "")))
                                                                .filter(Boolean)
                                                                .join(" | ")
                                                            : (
                                                                (() => {
                                                                    const du = (displayUser || {}) as any;
                                                                    const province = du?.province || "";
                                                                    const countryName = du?.country?.name || "";
                                                                    const composed = [province, countryName].filter(Boolean).join(", ");
                                                                    if (composed) return composed;
                                                                    return (profileData.personal.location && profileData.personal.location.length > 0)
                                                                        ? profileData.personal.location
                                                                        : t("myProfilePage.noLocation");
                                                                })()
                                                            )}
                                                    </div>
                                                    <div className="flex items-center gap-1">
                                                        {userReviewsLoading ? (
                                                            <div className="flex items-center gap-1">
                                                                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                                                                <span className="text-sm text-gray-500">{t("myProfilePage.loading")}</span>
                                                            </div>
                                                        ) : userReviews && userReviews.length > 0 ? (
                                                            (() => {
                                                                const ratingData = calculateUserAverageRating(userReviews, displayUserId);
                                                                return (
                                                                    <button
                                                                        onClick={() => setShowReviewModal(true)}
                                                                        className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                                                                        title={t("myProfilePage.clickToViewReviews")}
                                                                    >
                                                                        <StarRatingDisplay
                                                                            rating={ratingData.average}
                                                                            count={ratingData.count}
                                                                            size="sm"
                                                                        />
                                                                    </button>
                                                                );
                                                            })()
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <Star className="w-4 h-4 text-gray-300" />
                                                                <span className="text-sm text-gray-500">{t("myProfilePage.noReviewsYet")}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 text-sm text-gray-500">
                                                    <div className="flex items-center gap-1">
                                                        {t("myProfilePage.timezone")}
                                                        <h4 className="gap-2  items-center">
                                                            {generalSetting?.timezoneId?.name && (
                                                                <span>({generalSetting.timezoneId.name})</span>
                                                            )}{" "}
                                                        </h4>
                                                    </div>
                                                </div>
                                                {/* Favorite Button - Only show when authenticated user is viewing someone else's profile */}
                                                {(
                                                    <div className="flex items-center justify-center sm:justify-start gap-1 mt-2 text-sm text-gray-500">
                                                        <FavoriteButton
                                                            targetUserId={displayUserId}
                                                            size="sm"
                                                            showText={true}
                                                            className="p-0 rounded-none bg-transparent shadow-none hover:bg-transparent"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                                                {/* {isCurrentUser && !urlUserId ? (
                                                    <Button
                                                        onClick={() => setShowProfileModal(true)}
                                                        variant="outline"
                                                        className="hidden md:flex items-center gap-2">
                                                        <Edit className="w-4 h-4" />
                                                        Edit Profile
                                                    </Button>
                                                ) :  */}
                                                {!isCurrentUser || isViewing ? (
                                                    <Button
                                                        onClick={handleUnauthenticatedAction}
                                                        variant="outline"
                                                        className="flex items-center gap-2 w-full sm:w-auto">
                                                        <Edit className="w-4 h-4" />
                                                        {t("myProfilePage.hire")}
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    onClick={isCurrentUser ? handleShareProfile : handleUnauthenticatedAction}
                                                    className="flex items-center gap-2 w-full sm:w-auto">
                                                    {isCurrentUser ? (
                                                        <>
                                                            <Share2 className="w-4 h-4" />
                                                            {t("myProfilePage.share")}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <MessageSquare className="w-4 h-4" />
                                                            {t("myProfilePage.contact")}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6">
                                            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                                                <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">
                                                    {getDynamicCurrencySymbol()}{professionalStats?.balance || profileData.personal.totalEarned}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600">{t("myProfilePage.totalEarned")}</div>
                                            </div>
                                            <div className="bg-green-50 p-3 sm:p-4 rounded-lg">
                                                <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-600">
                                                    {professionalStats?.completedContractsCount || profileData.personal.completedProjects}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600">{t("myProfilePage.projectsCompleted")}</div>
                                            </div>
                                            <div className="bg-purple-50 p-3 sm:p-4 rounded-lg">
                                                <div className="text-lg sm:text-xl md:text-2xl font-bold text-purple-600">
                                                    {professionalStats?.totalOffers || profileData.personal.successRate}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600">{t("myProfilePage.totalOffers")}</div>
                                            </div>
                                            <div className="bg-orange-50 p-3 sm:p-4 rounded-lg">
                                                <div className="text-lg sm:text-xl md:text-2xl font-bold text-orange-600">
                                                    {getDynamicCurrencySymbol()}{profileData.personal.hourlyRate}
                                                </div>
                                                <div className="text-xs sm:text-sm text-gray-600">{t("myProfilePage.hourlyRate")}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8">
                                <div className="lg:w-2/3 space-y-4 sm:space-y-6">
                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <h2 className="text-lg sm:text-xl font-semibold">{t("myProfilePage.about")}</h2>
                                            {isCurrentUser && !isViewing && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-blue-600 hover:text-blue-700"
                                                    onClick={handlePersonalInfoEdit}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <p className="text-sm sm:text-base text-gray-600">{profileData.personal.bio}</p>
                                    </Card>

                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <CategoryDisplay
                                            selectedCategories={profileData.selectedCategories || []}
                                            selectedSubcategories={profileData.selectedSubcategories || []}
                                            onEdit={() => setShowProfileModal(true)}
                                            showEditIcon={isCurrentUser && !isViewing}
                                        />
                                    </Card>

                                    {/* Service Area Display - Only show for National Professional */}
                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <ServiceAreaDisplay
                                            userId={isAdminEdit ? adminUserId : displayUserId}
                                            selectedServiceAreas={profileData.selectedServiceAreas || []}
                                            onEdit={() => setShowProfileModal(true)}
                                            showEditIcon={isCurrentUser && !isViewing}
                                        />
                                    </Card>
                                    <CompanyClientComponent
                                        showEditIcon={isCurrentUser && !isViewing}
                                        isEditing={editingCompany}
                                        handleCompanyFieldChange={handleCompanyFieldChange}
                                        editedProfile={editedProfile}
                                        profileData={profileData}
                                        validationErrors={validationCompanyErrors.company}
                                        handleCancelEdit={handleCancelEdit}
                                        handleSaveChanges={handleCompanySaveChanges}
                                        setEditingCompany={startEditingCompany}
                                    />

                                    <Availability userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    <EmergencyAvailability
                                        userId={isAdminEdit ? adminUserId : displayUserId}
                                        isModalOpen={showAvailabilityModal}
                                        onModalOpen={() => setShowAvailabilityModal(true)}
                                        onModalClose={() => setShowAvailabilityModal(false)}
                                        showEmergencyHoursModal={showEmergencyHoursModal}
                                        onEmergencyHoursModalOpen={() => setShowEmergencyHoursModal(true)}
                                        onEmergencyHoursModalClose={() => setShowEmergencyHoursModal(false)}
                                        showEditIcon={isCurrentUser && !isViewing}
                                        onEmergencyHoursSaved={handleEmergencyHoursSaved}
                                    />
                                    <Skills userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    <Portfolio userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    
                                    {/* Achievements Section */}
                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <h2 className="text-lg font-semibold mb-4">{t("myProfilePage.achievements")}</h2>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            {calculateProfessionalAchievements()
                                                .filter(achievement => 
                                                    isCurrentUser 
                                                        ? true // Show all achievements for current user
                                                        : achievement.achieved // Show only achieved for other users
                                                )
                                                .map((achievement, index) => (
                                                    <div key={index} className={`p-3 rounded-lg ${
                                                        achievement.achieved 
                                                            ? "bg-green-50 border border-green-200" 
                                                            : "bg-gray-50 border border-gray-200"
                                                    }`}>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            {achievement.icon === "Star" && <Star className={`h-4 w-4 ${achievement.achieved ? "text-yellow-500" : "text-gray-400"}`} />}
                                                            {achievement.icon === "Award" && <Award className={`h-4 w-4 ${achievement.achieved ? "text-yellow-500" : "text-gray-400"}`} />}
                                                            {achievement.icon === "Shield" && <Shield className={`h-4 w-4 ${achievement.achieved ? "text-blue-500" : "text-gray-400"}`} />}
                                                            {achievement.icon === "DollarSign" && <DollarSign className={`h-4 w-4 ${achievement.achieved ? "text-green-500" : "text-gray-400"}`} />}
                                                            {achievement.icon === "Briefcase" && <Briefcase className={`h-4 w-4 ${achievement.achieved ? "text-purple-500" : "text-gray-400"}`} />}
                                                            <h3 className={`font-medium text-sm ${achievement.achieved ? "text-gray-900" : "text-gray-600"}`}>
                                                                {achievement.title}
                                                            </h3>
                                                        </div>
                                                        <p className="text-xs text-gray-600">
                                                            {achievement.description}
                                                        </p>
                                                    </div>
                                                ))}
                                        </div>
                                    </Card>
                                </div>

                                <div className="lg:w-1/3 space-y-4 sm:space-y-6">
                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <h2 className="text-lg sm:text-xl font-semibold">{t("myProfilePage.workCompleted")}</h2>
                                            {isCurrentUser && !isViewing && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-blue-600 hover:text-blue-700"
                                                    onClick={() => navigate("/myorders")}>
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="space-y-4">
                                            {professionalStatsLoading ? (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center animate-pulse">
                                                            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="h-4 sm:h-5 bg-gray-200 rounded w-32 sm:w-40 animate-pulse mb-2"></div>
                                                            <div className="h-3 sm:h-4 bg-gray-200 rounded w-40 sm:w-48 animate-pulse"></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="h-6 sm:h-8 bg-gray-200 rounded w-8 sm:w-12 animate-pulse"></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                                                                {t("myProfilePage.completedProjects", { count: professionalStats?.completedContractsCount || 0 })}
                                                            </h3>
                                                            <p className="text-xs sm:text-sm text-gray-500">
                                                                {t("myProfilePage.totalContractsDelivered")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-lg sm:text-2xl font-bold text-green-600">
                                                            {professionalStats?.completedContractsCount || 0}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                    <Card color={undefined} className="p-4 sm:p-6">
                                        <div className="flex justify-between items-start mb-4">
                                            <h2 className="text-lg sm:text-xl font-semibold">{t("myProfilePage.workInProgress")}</h2>
                                            {isCurrentUser && !isViewing && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-blue-600 hover:text-blue-700"
                                                    onClick={() => navigate("/myorders")}>
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="space-y-4">
                                            {professionalStatsLoading ? (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center animate-pulse">
                                                            <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-300 rounded"></div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="h-4 sm:h-5 bg-gray-200 rounded w-28 sm:w-36 animate-pulse mb-2"></div>
                                                            <div className="h-3 sm:h-4 bg-gray-200 rounded w-40 sm:w-48 animate-pulse"></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="h-6 sm:h-8 bg-gray-200 rounded w-8 sm:w-12 animate-pulse"></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                                                                {t("myProfilePage.activeProjects", { count: professionalStats?.activeContractsCount || 0 })}
                                                            </h3>
                                                            <p className="text-xs sm:text-sm text-gray-500">
                                                                {t("myProfilePage.currentlyWorking")}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <div className="text-lg sm:text-2xl font-bold text-blue-600">
                                                            {professionalStats?.activeContractsCount || 0}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                    {/* {displayUser && Number(displayUser.role.id) === RoleEnum.internationl_professional ? */}
                                    <Card color={undefined} className="p-6">
                                        <InternationalServiceAreas
                                            userId={isAdminEdit ? adminUserId : displayUserId}
                                            role={Number(displayUser?.role?.id)}
                                            selectedServiceAreas={profileData.selectedServiceAreas || []}
                                        />
                                    </Card>
                                    {/* :
                                        <LocationComponent
                                            editingLocation={editingLocation}
                                            isEditing={editingLocation}
                                            startEditingLocation={startEditingLocation}
                                            editedProfile={editedProfile}
                                            handleFieldChange={handleFieldChange}
                                            validationPersonalErrors={validationPersonalErrors}
                                            profileData={profileData}
                                            handleLocationSaveChanges={handleLocationSaveChanges}
                                            setEditingLocation={setEditingLocation}
                                            showEditIcon={isCurrentUser && !isViewing}
                                        />
                                    } */}


                                    <Certificate userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    <Education userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    {/* Show Contact Information Section if:
                                        1. User is viewing their own profile (isCurrentUser), OR
                                        2. User is authenticated and has contracts with escrows in status 2 or 3 
                                           where the contract's client ID matches the current user's ID */}
                                    {(isCurrentUser || hasAcceptedEscrows()) && (
                                        <ContactComponent
                                            editingContact={editingContact}
                                            isEditing={editingContact}
                                            startEditingContact={startEditingContact}
                                            editedProfile={editedProfile}
                                            handleCompanyFieldChange={handleCompanyFieldChange}
                                            validationCompanyErrors={validationCompanyErrors}
                                            handleSocialLinkChange={handleSocialLinkChange}
                                            profileData={profileData}
                                            validationPersonalErrors={validationPersonalErrors}
                                            handleContactSaveChanges={handleContactSaveChanges}
                                            setEditingContact={setEditingContact}
                                            showEditIcon={isCurrentUser && !isViewing}
                                            isCurrentUser={isCurrentUser}
                                            canEdit={isCurrentUser && !isViewing}
                                        />
                                    )}
                                    <LanguageComponent
                                        isLanguageEditing={isLanguageEditing}
                                        setIsLanguageEditing={setIsLanguageEditing}
                                        setEditingLanguages={setEditingLanguages}
                                        setHasAddedNewLanguage={setHasAddedNewLanguage}
                                        editingLanguages={editingLanguages}
                                        startEditingLanguage={startEditingLanguage}
                                        preferredLanguages={preferredLanguages}
                                        hasAddedNewLanguage={hasAddedNewLanguage}
                                        showEditIcon={isCurrentUser && !isViewing}
                                    />
                                    <Experience userId={isAdminEdit ? adminUserId : displayUserId} showEditIcon={isCurrentUser && !isViewing} />
                                    <Card color={undefined} className="p-4 sm:p-6 w-full">
                                        <h2 className="text-lg font-semibold mb-4">{t("myProfilePage.accountInfo")}</h2>
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">{t("myProfilePage.memberSince")}</span>
                                                <span className="font-medium text-right">{profileData.personal.joined}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">{t("myProfilePage.lastActive")}</span>
                                                <span className="font-medium text-right">{profileData.personal.lastActive}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">{t("myProfilePage.projectsPosted")}</span>
                                                <span className="font-medium text-right">{professionalStats?.totalOffers || 0}</span>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>

                        {/* Edit Modal */}
                        {showEditModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 md:p-6 z-50 overflow-y-auto">
                                <motion.div
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0.95, opacity: 0 }}
                                    className="relative w-full max-w-3xl mx-auto my-4 sm:my-8">
                                    <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                                        <button
                                            onClick={() => setShowEditModal(false)}
                                            className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-700 z-10">
                                            <X className="w-5 h-5 sm:w-6 sm:h-6" />
                                        </button>
                                    </div>
                                </motion.div>
                            </div>
                        )}

                        {/* Delete Account Confirmation Modal */}
                        {showDeleteConfirm && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
                                <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t("myProfilePage.deleteAccount")}</h3>
                                    <p className="text-gray-600 mb-6">
                                        {t("myProfilePage.deleteAccountConfirmation")}
                                    </p>
                                    <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                                        <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="w-full sm:w-auto">
                                            {t("myProfilePage.cancel")}
                                        </Button>
                                        <Button variant="destructive" onClick={() => setShowDeleteConfirm(false)} className="w-full sm:w-auto">
                                            {t("myProfilePage.delete")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <EditProfileModal
                            isOpen={showProfileModal}
                            onClose={() => setShowProfileModal(false)}
                            profileData={profileData.personal}
                            setProfileData={setProfileData}
                            isAdminEdit={isAdminEdit}
                            adminUserId={adminUserId}
                            onDataSaved={() => {
                                // Refresh user categories data when modal saves
                                const userId = isAdminEdit ? adminUserId : displayUserId;
                                if (userId) {
                                    fetchUserCategoriesForDisplay();
                                }

                                // Call admin callback if provided
                                if (isAdminEdit && onUserUpdated) {
                                    onUserUpdated();
                                }
                            }}
                        />

                        {/* Important Information Modal */}
                        <ImportantInformationModal
                            isOpen={showImportantInfoModal}
                            onClose={() => setShowImportantInfoModal(false)}
                            onUnderstand={handleUnderstandClick}
                        />

                        {/* Emergency Hours Modal */}
                        <EmergencyHoursModal
                            isOpen={showEmergencyHoursModal}
                            onClose={() => setShowEmergencyHoursModal(false)}
                            onConfirm={handleEmergencyHoursConfirm}
                        />
                        <br></br>
                        <br></br>
                        <br></br>
                        <br></br>
                    </main>

                </div>

                {/* Review Modal */}
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => setShowReviewModal(false)}
                    professional={displayUser ? {
                        id: displayUser.id,
                        name: displayUser.name || `${displayUser.firstName || ""} ${displayUser.lastName || ""}`.trim(),
                        firstName: displayUser.firstName,
                        lastName: displayUser.lastName,
                        title: displayUser.title,
                        photo: displayUser.photo,
                        averageRating: userReviews && userReviews.length > 0 ? calculateUserAverageRating(userReviews, displayUserId).average : 0,
                        reviewsCount: userReviews ? userReviews.length : 0,
                        reviewsList: userReviews || []
                    } : null}
                />

                {/* Contract Milestone Modal */}
                <ContractMilestoneModal
                    isOpen={showContractMilestoneModal}
                    onClose={() => {
                        setShowContractMilestoneModal(false);
                        setSelectedOffer(null);
                    }}
                    offer={selectedOffer}
                    clientOffers={clientOffers}
                    onOfferSelect={(offer) => {
                        setSelectedOffer(offer);
                    }}
                />
                {/* Footer */}
                {!isAdminEdit && (
                    <footer className={`bg-white border-t mt-auto w-full ${isCurrentUser ? "md:pl-56 xl:pl-64" : ""}`}>
                        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
                            <SharedFooter3 />
                        </div>
                    </footer>
                )}

            </div>
        </OrientationLock>
    );
};

export default MyProfile;
